// Angular Imports
import { Component, OnInit, Input, Injector } from '@angular/core';

// CC-Common imports
import { IconConstants } from 'cc-common';

// 3rd party imports
import notify from 'devextreme/ui/notify';

// Component imports
import { MPSearchBaseComponent } from '../_base/mp-base-search.component';

// Constant imports
import { MessageInterval } from '../../_constants/messages-intervals';
import { StringFormatConstants } from '../../_constants/string-format-constants';

// Service imports
import { ServerErrorService } from '../../_services/shared-services/server-error.service';
import { SearchService } from '../_services/data-services/search.service';

// Model imports
import { SearchParametersDto } from '../_models/search-parameters-dto';
import { WorkOrderSearchResultDto } from '../_models/work-order-search-result-dto';
import { BaseProcessNotesResultDto } from '../../management-common/_models/notes/base-process-notes-result-dto';
import { WorkOrderHistoryResultDto } from '../_models/work-order-history-result-dto';
import { BaseSearchHistoryParametersDto } from '../_models/base-search-history-parameters-dto';
import { BaseSearchNotesParametersDto } from '../_models/base-search-notes-parameters-dto';
import { BaseSearchAddNotesParametersDto } from '../_models/base-search-add-notes-parameters-dto';

@Component({
    selector: 'app-search-work-orders',
    templateUrl: './search-work-orders.component.html',
    styleUrls: ['./search-work-orders.component.scss']
})
export class SearchWorkOrdersComponent extends MPSearchBaseComponent implements OnInit {
    @Input() communityId: number;
    @Input() startDate: Date;
    @Input() endDate: Date;

    workOrders: WorkOrderSearchResultDto[] = [];

    gridRowIndex: number;
    showDescription = false;

    iconNotes: string = IconConstants.NOTES_AVAILABLE;
    iconEmptyNotes: string = IconConstants.NOTES_EMPTY;
    iconLog = IconConstants.HISTORY;

    showNotesEditor = false;
    previousNotes: BaseProcessNotesResultDto = new BaseProcessNotesResultDto();

    showTransactionLog = false;
    transactionLogData: WorkOrderHistoryResultDto[] = [];
    workOrdersColumnDef: any[] = [];
    dateTimeFormat = StringFormatConstants.dateTimeFormat;

    workOrderHistoryParam: BaseSearchHistoryParametersDto = new BaseSearchHistoryParametersDto();
    workOrderNotesParam: BaseSearchNotesParametersDto = new BaseSearchNotesParametersDto();

    constructor(
        protected readonly injector: Injector,
        private readonly errorService: ServerErrorService,
        private readonly searchService: SearchService
    ) {
        super(injector);
    }

    ngOnInit() {
        if (!!this.searchText) {
            this.searchWorkOrders();
        }
    }

    getCommunityId(groupdata: any): number {
        let communityId = null;
        if (!!groupdata && !!groupdata.items && groupdata.items.length > 0) {
            communityId = groupdata.items[0].communityId;
        } else if (!!groupdata && !!groupdata.collapsedItems && groupdata.collapsedItems.length > 0) {
            communityId = groupdata.collapsedItems[0].communityId;
        }
        return communityId;
    }

    toggleDescription(row: any): void {
        if (!!row) {
            this.gridRowIndex = row.rowIndex;
            this.showDescription = !this.showDescription;
        }
    }

    onNotesClick(data: WorkOrderSearchResultDto) {
        if (!!data) {
            this.workOrderNotesParam.communityId = data.communityId;
            this.workOrderNotesParam.processId = data.workOrderId;
            this.workOrderNotesParam.clearCache = true;
            this.loadingVisible = true;
            this.searchService.getWorkOrderNotes(this.workOrderNotesParam).subscribe(
                (result: BaseProcessNotesResultDto) => {
                    this.previousNotes = result;
                    this.showNotesEditor = true;
                    this.loadingVisible = false;
                },
                (error) => {
                    this.errorService.displayError(error);
                    this.loadingVisible = false;
                }
            );
        }
    }

    onNotesEditorClose(): void {
        this.showNotesEditor = false;
    }

    onTransactionLogclose(): void {
        this.showTransactionLog = false;
    }

    onNoteSave(event: string): void {
        if (!!event) {
            const param: BaseSearchAddNotesParametersDto = new BaseSearchAddNotesParametersDto();
            param.communityId = this.workOrderNotesParam.communityId;
            param.processId = this.workOrderNotesParam.processId;
            param.note = event;
            param.clearCache = true;
            this.loadingVisible = true;
            this.searchService.addWorkOrderNotes(param).subscribe(
                () => {
                    notify('Work Order notes successfully saved.', 'success', MessageInterval.successMsgInterval);
                    this.loadingVisible = false;
                    this.showNotesEditor = false;
                    this.searchWorkOrders();
                },
                (error) => {
                    this.errorService.displayError(error);
                    this.loadingVisible = false;
                }
            );
        }
    }

    onLogClick(data: WorkOrderSearchResultDto): void {
        if (!!data) {
            this.getWorkOrderHistoryColumns();
            this.workOrderHistoryParam.communityId = data.communityId;
            this.workOrderHistoryParam.processId = data.workOrderId;
            this.workOrderHistoryParam.clearCache = true;
            this.loadingVisible = true;
            this.searchService.getWorkOrderHistory(this.workOrderHistoryParam).subscribe(
                (result: WorkOrderHistoryResultDto[]) => {
                    this.transactionLogData = result;
                    this.showTransactionLog = true;
                    this.loadingVisible = false;
                },
                (error) => {
                    this.errorService.displayError(error);
                    this.loadingVisible = false;
                }
            );
        }
    }

    searchWorkOrders() {
        const param: SearchParametersDto = new SearchParametersDto();
        param.clearCache = true;
        param.searchText = this.searchText;
        param.communityId = this.communityId;
        param.startDate = this.startDate;
        param.endDate = this.endDate;
        this.workOrders = [];
        this.loadingVisible = true;
        this.searchService.searchWorkOrders(param).subscribe(
            (data: WorkOrderSearchResultDto[]) => {
                this.workOrders = data;
                this.loadingVisible = false;
                this.updateGridSearchStatus(data);
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private getWorkOrderHistoryColumns(): void {
        this.workOrdersColumnDef = [
            {
                dataField: 'beginValue',
                caption: 'Begin Value',
                dataType: 'string'
            },
            {
                dataField: 'endValue',
                caption: 'End Value',
                dataType: 'string'
            },
            {
                dataField: 'modified',
                caption: 'Modified',
                dataType: 'string'
            },
            {
                dataField: 'user',
                caption: 'User',
                dataType: 'string',
                width: '130',
                cellTemplate: 'userBioTemplate'
            },
            {
                dataField: 'date',
                caption: 'Date',
                dataType: 'date',
                width: '145',
                format: this.dateTimeFormat
            },
            {
                dataField: 'additionalInfo',
                dataType: 'string',
                caption: 'Additional Information'
            }
        ];
    }
}
